<template>
  <v-dialog :value="value" fullscreen persistent hide-overlay transition="dialog-bottom-transition" @keydown.esc="close">
    <v-card v-if="reviewStage && sourceStage" class="d-flex flex-column feedback-card">
      <a href="" ref="fileDownload" class="d-none"></a>
      <v-toolbar dark color="primary" class="flex-grow-0">
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t('components.StageFeedbackDialog.title', { stage: reviewStage.name }) }}</v-toolbar-title>
      </v-toolbar>
      <div ref="body" class="d-flex feedback-body" :class="{ 'feedback-body--dragging': dragging }">
        <div v-if="reports.length > 0" class="d-flex flex-column feedback-report" :style="{ flex: editorCollapsed ? '1 1 auto' : '0 0 ' + reportWidth + '%' }">
          <div class="d-flex align-center px-4 py-2 feedback-report-bar">
            <v-icon color="blue" class="mr-2">mdi-file-chart-outline</v-icon>
            <span class="text-subtitle-2 mr-4">{{ sourceStage.name }}</span>
            <v-select
              v-model="reportVersion"
              :items="reportItems"
              :label="$t('components.StageFeedbackDialog.reportVersion')"
              dense
              outlined
              hide-details
              class="feedback-report-select"
            ></v-select>
          </div>
          <iframe :src="reportUrl" frameborder="0" sandbox="allow-scripts" class="flex-grow-1 feedback-report-frame"></iframe>
        </div>
        <!-- Dragging resizes the panes; a click without dragging hides the feedback, or brings it back as it was -->
        <div
          v-if="reports.length > 0"
          class="d-flex flex-column align-center justify-center feedback-divider"
          :class="{ 'feedback-divider--collapsed': editorCollapsed, 'feedback-divider--dragging': dragging }"
          role="separator"
          aria-orientation="vertical"
          :aria-valuenow="Math.round(reportWidth)"
          tabindex="0"
          :title="editorCollapsed ? $t('components.StageFeedbackDialog.showFeedback') : $t('components.StageFeedbackDialog.dividerHint')"
          @pointerdown="startDrag"
          @keydown.enter.prevent="toggleEditor"
          @keydown.space.prevent="toggleEditor"
          @keydown.left.prevent="nudgeDivider(-5)"
          @keydown.right.prevent="nudgeDivider(5)"
        >
          <template v-if="editorCollapsed">
            <v-icon small color="purple">mdi-comment-text-outline</v-icon>
            <span v-if="activeMessageCount > 0" class="mt-1 text-caption font-weight-bold purple--text">{{ activeMessageCount }}</span>
            <v-icon small>mdi-chevron-left</v-icon>
            <span class="text-caption font-weight-medium feedback-divider-label">{{ $t('components.StageFeedbackDialog.feedbackTab') }}</span>
          </template>
          <div v-else class="feedback-divider-grip"></div>
        </div>
        <!-- No d-flex here: its display: flex !important would override v-show -->
        <div v-show="!editorCollapsed" class="feedback-editor" :class="{ 'feedback-editor--alone': reports.length === 0 }">
          <div class="px-4 pt-4 pb-2 feedback-chat-header">
            <div class="text-h6">{{ $t('components.StageFeedbackDialog.feedbackOn', { stage: sourceStage.name }) }}</div>
            <div v-for="line in spreadsheetLines" :key="line.kind" class="d-flex align-center flex-wrap mt-1">
              <span class="text-caption mr-2">{{ line.label }}:</span>
              <v-tooltip bottom v-for="spreadsheet in line.versions" :key="spreadsheet.url">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip small v-bind="attrs" v-on="on" class="mr-2 my-1"
                    :color="spreadsheet.latest ? line.config.color : 'gray'"
                    :text-color="spreadsheet.latest ? 'white' : 'grey'"
                    @click="download(spreadsheet.url)"
                  >
                    <v-icon class="mr-2" small>{{ line.config.icon }}</v-icon>
                    <b class="text-button">v{{ spreadsheet.version }}</b>&nbsp;<span class="text-caption">{{ spreadsheet.date }}</span>
                  </v-chip>
                </template>
                <span>{{ $t('components.StageFeedbackDialog.downloadDeliverable', { name: line.label }) }}</span>
                <div v-if="spreadsheet.name" class="text-caption">{{ spreadsheet.name }}</div>
              </v-tooltip>
            </div>
          </div>
          <div ref="messageList" class="px-4 py-2 feedback-messages" @scroll="onMessagesScroll">
            <div v-if="messages.length === 0" class="text-body-2 grey--text py-4">{{ $t('components.StageFeedbackDialog.hint') }}</div>
            <div v-for="message in messages" :key="message.id" class="d-flex my-2" :class="isOwn(message) ? 'justify-end' : 'justify-start'">
              <div class="feedback-message" :class="{ 'feedback-message--own': isOwn(message), 'feedback-message--staff': message.isStaff && !isOwn(message), 'feedback-message--deleted': !!message.deletedAt }">
                <div class="d-flex align-center text-caption">
                  <span class="font-weight-bold">{{ isOwn(message) ? $t('components.StageFeedbackDialog.you') : message.createdByName }}</span>
                  <v-tooltip v-if="message.isStaff" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon x-small class="ml-1" v-bind="attrs" v-on="on">mdi-account-tie</v-icon>
                    </template>
                    <span>{{ $t('components.StageFeedbackDialog.staff') }}</span>
                  </v-tooltip>
                  <span class="ml-2 grey--text">{{ formatFeedbackTime(message.createdAt) }}</span>
                  <v-tooltip v-if="message.reportVersion && !message.deletedAt && reports.length > 0" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip x-small v-bind="attrs" v-on="on" class="ml-2" color="blue" text-color="white" @click="reportVersion = message.reportVersion">v{{ message.reportVersion }}</v-chip>
                    </template>
                    <span>{{ $t('components.StageFeedbackDialog.onReport', { version: message.reportVersion }) }}</span>
                  </v-tooltip>
                  <v-spacer></v-spacer>
                  <v-menu v-if="isOwn(message) && !message.deletedAt && editingId !== message.id" offset-y left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small class="ml-2" v-bind="attrs" v-on="on" :loading="busyId === message.id" :disabled="!!busyId">
                        <v-icon small>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="startEdit(message)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-pencil</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('components.StageFeedbackDialog.edit') }}</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="remove(message)">
                        <v-list-item-icon class="mr-2"><v-icon small>mdi-delete-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ $t('components.StageFeedbackDialog.delete') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
                <div v-if="message.deletedAt" class="text-body-2 font-italic grey--text">
                  <v-icon small class="mr-1">mdi-delete-outline</v-icon>{{ $t('components.StageFeedbackDialog.deleted', { name: message.createdByName, date: formatFeedbackTime(message.deletedAt) }) }}
                </div>
                <div v-else-if="editingId === message.id" class="mt-1">
                  <rich-text-editor
                    v-model="editText"
                    :max-length="maxLength"
                    min-height="48px"
                    autofocus
                    @submit="saveEdit(message)"
                  ></rich-text-editor>
                  <div class="d-flex align-center mt-1">
                    <v-spacer></v-spacer>
                    <v-btn x-small text :disabled="busyId === message.id" @click="cancelEdit">{{ $t('components.StageFeedbackDialog.cancel') }}</v-btn>
                    <v-btn x-small color="primary" class="ml-1" :loading="busyId === message.id" :disabled="!canSaveEdit(message)" @click="saveEdit(message)">{{ $t('components.StageFeedbackDialog.save') }}</v-btn>
                  </div>
                </div>
                <!-- markdownToHtml escapes the text and only adds its own formatting tags -->
                <div v-else class="text-body-2 feedback-message-text" v-html="messageHtml(message)"></div>
                <div v-if="message.editedAt && !message.deletedAt && editingId !== message.id" class="text-caption grey--text">
                  {{ $t('components.StageFeedbackDialog.edited', { date: formatFeedbackTime(message.editedAt) }) }}
                </div>
              </div>
            </div>
          </div>
          <div class="px-4 pb-4 pt-2 feedback-composer">
            <rich-text-editor
              ref="composer"
              v-model="text"
              :placeholder="$t('components.StageFeedbackDialog.placeholder')"
              :max-length="maxLength"
              class="mb-2"
              @submit="send"
            ></rich-text-editor>
            <v-alert v-if="error" type="error" dense text class="mb-2">{{ error }}</v-alert>
            <div class="d-flex align-center">
              <span class="text-caption grey--text">{{ $t('components.StageFeedbackDialog.sendHint') }}</span>
              <v-spacer></v-spacer>
              <v-btn color="primary" :disabled="!canSend" :loading="sending" @click="send">
                <v-icon left small>mdi-send</v-icon>
                {{ $t('components.StageFeedbackDialog.send') }}
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { formatFeedbackTime } from '@/utils/stageFeedback'
import { markdownToHtml } from '@/utils/feedbackMarkdown'
import RichTextEditor from '@/components/RichTextEditor'
import { DELIVERABLE_KINDS, SPREADSHEET_KINDS } from '@/utils/stageDeliverables'

// Same limit as saveStageFeedback in functions/index.js
const MAX_LENGTH = 20000
// Report pane width (% of the dialog) it opens with, and how narrow each pane can get
const DEFAULT_REPORT_WIDTH = 65
const MIN_PANE_WIDTH = 320
// Pointer travel under which pressing the divider counts as a click, not a drag
const CLICK_TOLERANCE = 3
// How close to the end of the chat counts as reading the latest messages, so new ones scroll into view
const NEAR_BOTTOM = 80

export default {
  components: {
    RichTextEditor
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    // The Review stage whose chat this is
    reviewStage: {
      type: Object,
      default: null
    },
    // The stage right before it, whose deliverables are being reviewed
    sourceStage: {
      type: Object,
      default: null
    },
    // The chat's messages, oldest first, kept up to date by the store's listener
    messages: {
      type: Array,
      default: () => []
    },
    // Report version to open on instead of the latest one, e.g. the one clicked to view it
    initialReportVersion: {
      type: Number,
      default: null
    },
    // Opens with the feedback hidden, for when the report was opened to read it
    startCollapsed: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      text: '',
      reportVersion: null,
      sending: false,
      error: null,
      editingId: null,
      editText: '',
      busyId: null,
      nearBottom: true,
      maxLength: MAX_LENGTH,
      reportWidth: DEFAULT_REPORT_WIDTH,
      editorCollapsed: false,
      dragging: false,
      dragStartX: 0,
      dragMoved: false
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    reports () {
      return this.versions(DELIVERABLE_KINDS.report)
    },
    // One line per spreadsheet kind that has versions
    spreadsheetLines () {
      return SPREADSHEET_KINDS.map(kind => {
        const config = DELIVERABLE_KINDS[kind]
        return {
          kind: kind,
          config: config,
          label: this.$t(`views.ModelingProcess.deliverableKinds.${config.labelKey}`),
          versions: this.versions(config)
        }
      }).filter(line => line.versions.length > 0)
    },
    reportItems () {
      return this.reports.slice().reverse().map(report => ({
        value: report.version,
        text: `v${report.version}` + (report.date ? ` · ${report.date}` : '') + (report.latest ? ` (${this.$t('components.StageFeedbackDialog.latest')})` : '')
      }))
    },
    reportUrl () {
      const report = this.reports.find(item => item.version === this.reportVersion)
      return report ? report.url : ''
    },
    activeMessageCount () {
      return this.messages.filter(message => !message.deletedAt).length
    },
    editingMessage () {
      return this.editingId ? this.messages.find(message => message.id === this.editingId) || null : null
    },
    canSend () {
      return this.text.trim().length > 0 && this.text.trim().length <= this.maxLength && !this.sending
    },
    // Unsent text in the composer, or an edit in progress that changes its message
    dirty () {
      return this.text.trim().length > 0 || (!!this.editingMessage && this.editText.trim() !== this.editingMessage.text)
    }
  },
  methods: {
    ...mapActions('clients', [
      'saveStageFeedback',
      'deleteStageFeedback'
    ]),
    formatFeedbackTime: formatFeedbackTime,
    messageHtml (message) {
      return markdownToHtml(message.text)
    },
    versions (config) {
      const urls = (this.sourceStage && this.sourceStage[config.urlsKey]) || []
      const dates = (this.sourceStage && this.sourceStage[config.datesKey]) || {}
      const names = (this.sourceStage && this.sourceStage[config.namesKey]) || {}
      return urls.map((url, index) => ({
        version: index + 1,
        url: url,
        date: this.formatDate(dates[url]),
        name: names[url] || null,
        latest: index === urls.length - 1
      }))
    },
    formatDate (timestamp) {
      if (!timestamp || !timestamp.toDate) {
        return ''
      }
      return timestamp.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },
    isOwn (message) {
      return message.createdBy === this.authUser.uid
    },
    reset () {
      this.text = ''
      // A report opened to view it opens on that version, otherwise on the latest
      const known = this.reports.some(report => report.version === this.initialReportVersion)
      this.reportVersion = known ? this.initialReportVersion : (this.reports.length > 0 ? this.reports.length : null)
      this.error = null
      this.sending = false
      this.editingId = null
      this.editText = ''
      this.busyId = null
      // Without a report there's nothing to show in place of the feedback
      this.editorCollapsed = this.startCollapsed && this.reports.length > 0
      this.scrollToBottom()
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const list = this.$refs.messageList
        if (list) {
          list.scrollTop = list.scrollHeight
        }
        this.nearBottom = true
      })
    },
    onMessagesScroll () {
      const list = this.$refs.messageList
      this.nearBottom = list.scrollHeight - list.scrollTop - list.clientHeight <= NEAR_BOTTOM
    },
    send () {
      if (!this.canSend) {
        return
      }
      this.sending = true
      this.error = null
      this.saveStageFeedback({
        stageName: this.reviewStage.name,
        text: this.text.trim(),
        reportVersion: this.reportVersion
      }).then(() => {
        this.sending = false
        this.text = ''
        // The message shows up through the listener, which may have delivered it already
        this.scrollToBottom()
      }).catch(error => {
        console.error(error)
        this.sending = false
        this.error = this.$t('components.StageFeedbackDialog.errorSaving')
      })
    },
    startEdit (message) {
      this.editingId = message.id
      this.editText = message.text
      this.error = null
    },
    cancelEdit () {
      this.editingId = null
      this.editText = ''
    },
    canSaveEdit (message) {
      const text = this.editText.trim()
      return text.length > 0 && text.length <= this.maxLength && text !== message.text && this.busyId !== message.id
    },
    saveEdit (message) {
      if (!this.canSaveEdit(message)) {
        return
      }
      this.busyId = message.id
      this.error = null
      this.saveStageFeedback({
        stageName: this.reviewStage.name,
        text: this.editText.trim(),
        messageId: message.id
      }).then(() => {
        this.busyId = null
        this.cancelEdit()
      }).catch(error => {
        console.error(error)
        this.busyId = null
        this.error = this.$t('components.StageFeedbackDialog.errorSaving')
      })
    },
    remove (message) {
      if (!confirm(this.$t('components.StageFeedbackDialog.deleteConfirm'))) {
        return
      }
      this.busyId = message.id
      this.error = null
      this.deleteStageFeedback({
        messageId: message.id
      }).then(() => {
        this.busyId = null
      }).catch(error => {
        console.error(error)
        this.busyId = null
        this.error = this.$t('components.StageFeedbackDialog.errorDeleting')
      })
    },
    startDrag (event) {
      if (event.button !== 0) {
        return
      }
      event.preventDefault()
      this.dragging = true
      this.dragStartX = event.clientX
      this.dragMoved = false
      window.addEventListener('pointermove', this.onDrag)
      window.addEventListener('pointerup', this.endDrag)
      window.addEventListener('pointercancel', this.endDrag)
    },
    onDrag (event) {
      if (!this.dragMoved && Math.abs(event.clientX - this.dragStartX) <= CLICK_TOLERANCE) {
        return
      }
      this.dragMoved = true
      // Dragging a hidden feedback out brings it back at the pointer's position
      this.editorCollapsed = false
      this.setReportWidth(event.clientX)
    },
    endDrag () {
      window.removeEventListener('pointermove', this.onDrag)
      window.removeEventListener('pointerup', this.endDrag)
      window.removeEventListener('pointercancel', this.endDrag)
      if (this.dragging && !this.dragMoved) {
        this.toggleEditor()
      }
      this.dragging = false
    },
    // Report width for a divider at clientX, leaving both panes at least MIN_PANE_WIDTH wide
    setReportWidth (clientX) {
      const rect = this.$refs.body.getBoundingClientRect()
      const min = Math.min(MIN_PANE_WIDTH, rect.width / 2)
      const x = Math.min(Math.max(clientX - rect.left, min), rect.width - min)
      this.reportWidth = x / rect.width * 100
    },
    nudgeDivider (percent) {
      if (this.editorCollapsed) {
        return
      }
      const rect = this.$refs.body.getBoundingClientRect()
      this.setReportWidth(rect.left + rect.width * (this.reportWidth + percent) / 100)
    },
    // Hiding keeps reportWidth, so the feedback comes back at the width it had
    toggleEditor () {
      this.editorCollapsed = !this.editorCollapsed
      if (!this.editorCollapsed) {
        this.$nextTick(() => {
          if (this.$refs.composer) {
            this.$refs.composer.focus()
          }
        })
      }
    },
    download (url) {
      // Spreadsheets are stored as attachments, so following the link downloads them
      this.$refs.fileDownload.href = url
      this.$refs.fileDownload.click()
    },
    close () {
      if (this.sending || this.busyId) {
        return
      }
      if (this.dirty && !confirm(this.$t('components.StageFeedbackDialog.discardConfirm'))) {
        return
      }
      this.$emit('input', false)
    }
  },
  beforeDestroy () {
    this.endDrag()
  },
  watch: {
    value: {
      immediate: true,
      handler (open) {
        if (open) {
          this.reset()
        }
      }
    },
    // A hidden list can't be scrolled, so it's brought to the latest messages once shown
    editorCollapsed (collapsed) {
      if (!collapsed) {
        this.scrollToBottom()
      }
    },
    messages (messages, previous) {
      const last = messages[messages.length - 1]
      const arrived = messages.length > previous.length && !!last
      // New messages scroll into view when reading the latest ones, and always when they're the user's
      if (arrived && (this.nearBottom || this.isOwn(last))) {
        this.scrollToBottom()
      }
      // A message deleted while being edited, e.g. from another tab, can't be saved anymore
      if (this.editingId && !(this.editingMessage && !this.editingMessage.deletedAt)) {
        this.cancelEdit()
      }
    }
  }
}
</script>
<style scoped>
.feedback-card {
  height: 100vh;
}
.feedback-body {
  flex: 1 1 auto;
  min-height: 0;
}
.feedback-report {
  min-width: 0;
}
/* While dragging, the report's iframe would swallow the pointer and stop the drag */
.feedback-body--dragging {
  cursor: col-resize;
  user-select: none;
}
.feedback-body--dragging .feedback-report-frame {
  pointer-events: none;
}
.feedback-divider {
  flex: 0 0 9px;
  cursor: col-resize;
  touch-action: none;
  background: rgba(0, 0, 0, 0.04);
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  transition: background-color 0.15s;
  outline: none;
}
.feedback-divider:hover,
.feedback-divider:focus-visible,
.feedback-divider--dragging {
  background: rgba(0, 0, 0, 0.12);
}
.feedback-divider-grip {
  width: 3px;
  height: 36px;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.35);
}
/* Hidden feedback: the divider becomes a tab to bring it back */
.feedback-divider--collapsed {
  flex-basis: 32px;
  cursor: pointer;
  justify-content: flex-start !important;
  padding-top: 16px;
}
.feedback-divider-label {
  writing-mode: vertical-rl;
  margin-top: 8px;
}
.feedback-report-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.feedback-report-select {
  max-width: 280px;
}
.feedback-report-frame {
  width: 100%;
}
/* Chat column: header and composer keep their height, the messages scroll in between */
.feedback-editor {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.feedback-editor--alone {
  max-width: 800px;
  margin: 0 auto;
}
.feedback-chat-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.feedback-messages {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.02);
}
.feedback-composer {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.feedback-message {
  max-width: 85%;
  min-width: 200px;
  padding: 8px 12px;
  border-radius: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.feedback-message--own {
  background: #f3e5f5;
  border-color: #e1bee7;
}
.feedback-message--staff {
  background: #e3f2fd;
  border-color: #bbdefb;
}
.feedback-message--deleted {
  background: transparent;
  border-style: dashed;
}
.feedback-message-text {
  white-space: pre-wrap;
  word-break: break-word;
}
.feedback-message-text ::v-deep ul,
.feedback-message-text ::v-deep ol {
  padding-left: 24px;
  margin: 2px 0;
}
</style>
